import { ContactTypeIcon } from "@/components/contact/ContactTypeIcon";
import AntIcon from "@/components/icons/AntIcon";
import { List } from "@/components/list/List";
import { CalendarPanel } from "@/components/views/CalendarPanel";
import CopyToClipboardView from "@/components/views/CopyToClipboardView";
import Ellipsis from "@/components/views/Ellipsis";
import { formatAggregatedName, formatPhoneNumber } from "@/utils/formatUtils";
import { cssVariables, dateToIsoDateString } from "@/utils/utils";
import { Avatar, Col, Divider, Flex, Row, Space, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { useGetDashboardPersonalDates } from "../../../../../common/api/queries";
import { ContactType } from "../../../../../common/enums";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { CLIENT_ROUTE_PATHS } from "../../../../client/paths";

export const DashboardPersonalDatesWidget = () => {
  const { t } = useTranslation();

  const [calendarDate, setCalendarDate] = useState<Dayjs>(() => dayjs());

  const { data, isFetching } = useGetDashboardPersonalDates({
    minDate: dateToIsoDateString(calendarDate.startOf("day")),
    maxDate: dateToIsoDateString(calendarDate.endOf("day")),
  });

  const selectedDayPersons = data?.personalDates[dateToIsoDateString(calendarDate)] || [];

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Flex justify="space-between" align="end">
        <h2 style={{ fontWeight: 600, fontSize: 28, lineHeight: "36px" }}>{t("dashboard.personalDates.card")}</h2>

        <div style={{ marginBottom: 14 }}>
          <span className="sub-header-info">{t("dashboard.personalDates.todayNameDays")}: </span>
          <b>{data?.todayNameDays.length ? data?.todayNameDays.join(", ") : "-"}</b>
        </div>
      </Flex>

      <div style={{ height: "100%" }}>
        <Row gutter={24}>
          <Col span={10}>
            <CalendarPanel
              style={{ padding: 16, borderRadius: 18 }}
              value={calendarDate}
              onChange={setCalendarDate}
              data={data?.personalDates}
            />
          </Col>

          <Col
            span={14}
            style={
              selectedDayPersons.length === 0
                ? {
                    borderRadius: 18,
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }
                : undefined
            }
          >
            <List
              pagination={{
                pageSize: 3,
                size: "small",
                hideOnSinglePage: true,
                showSizeChanger: false,
              }}
              loading={isFetching}
              gap={16}
              showEmpty
              items={selectedDayPersons}
              rowKey={(item) => item.id}
              renderItem={(item) => (
                <Flex
                  align="center"
                  justify="space-between"
                  style={{ backgroundColor: "white", borderRadius: 18, padding: 16 }}
                >
                  <Flex align="start" gap={16} style={{ flex: 1, maxWidth: "100%" }}>
                    {item.clientType ? (
                      <Tooltip title={t("dashboard.personalDatesExtended.helpers.client")}>
                        <Avatar style={{ backgroundColor: cssVariables.colorOrange }} icon={<AntIcon type="team" />} />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("dashboard.personalDatesExtended.helpers.agent")}>
                        <Avatar
                          style={{ backgroundColor: cssVariables.colorYellow }}
                          icon={<AntIcon type="user-switch" />}
                        />
                      </Tooltip>
                    )}

                    <div style={{ flex: "1 0", width: 0 }}>
                      <h4 style={{ margin: "0 0 var(--ant-margin-xxs)" }}>
                        <Ellipsis>
                          <Link
                            className="no-link-color"
                            to={generatePath(
                              item.clientType ? CLIENT_ROUTE_PATHS.detail.to : AGENT_ROUTE_PATHS.detail.to,
                              {
                                id: item.id,
                              },
                            )}
                            target="_blank"
                          >
                            {formatAggregatedName(item)}
                          </Link>
                        </Ellipsis>
                      </h4>

                      <div style={{ color: "var(--ant-color-text-description)" }}>
                        <Row>
                          {item.phone ? (
                            <>
                              <Col span={10}>
                                <Flex align="start">
                                  <span>
                                    <ContactTypeIcon type={ContactType.PHONE_NUMBER} />
                                  </span>

                                  <CopyToClipboardView
                                    content={item.phone}
                                    style={{ minWidth: 0, overflow: "hidden", display: "inline-flex" }}
                                    wrapper={
                                      <div style={{ display: "inline-flex", overflow: "hidden" }}>
                                        <Ellipsis style={{ display: "inline-block" }}>
                                          <span style={{ marginLeft: 8 }}>{formatPhoneNumber(item.phone)}</span>
                                        </Ellipsis>
                                      </div>
                                    }
                                  ></CopyToClipboardView>
                                </Flex>
                              </Col>

                              <Col>
                                <Divider orientation="vertical" />
                              </Col>
                            </>
                          ) : undefined}

                          {item.email ? (
                            <Col span={12} style={{ position: "relative" }}>
                              <Flex align="start">
                                <span>
                                  <ContactTypeIcon type={ContactType.EMAIL} />
                                </span>

                                <CopyToClipboardView
                                  content={item.email}
                                  style={{ minWidth: 0, overflow: "hidden", display: "inline-flex" }}
                                  wrapper={
                                    <div style={{ display: "inline-flex", overflow: "hidden" }}>
                                      <Ellipsis style={{ display: "inline-block" }}>
                                        <span style={{ marginLeft: 8 }}>{item.email}</span>
                                      </Ellipsis>
                                    </div>
                                  }
                                ></CopyToClipboardView>
                              </Flex>
                            </Col>
                          ) : undefined}
                        </Row>
                      </div>
                    </div>
                  </Flex>

                  <Space size="small">
                    {item.hasNameDay && (
                      <Tooltip title={t("dashboard.personalDates.nameDay")}>
                        <span>
                          <AntIcon size="huge" color="green" type="contact" />
                        </span>
                      </Tooltip>
                    )}

                    {item.hasBirthday && (
                      <Tooltip title={t("dashboard.personalDates.birthday")}>
                        <span>
                          <AntIcon size="huge" color="red" type="gift" />
                        </span>
                      </Tooltip>
                    )}
                  </Space>
                </Flex>
              )}
            />
          </Col>
        </Row>
      </div>
    </Flex>
  );
};
